import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { mockData } from "../mock";
import { submitContact } from "../lib/api";

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(formData);
      toast.success("Message sent. Our team will get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    { icon: <Phone className="h-5 w-5" />, title: "Phone", content: mockData.contact.phone, href: `tel:${mockData.contact.phone}` },
    { icon: <Mail className="h-5 w-5" />, title: "Email", content: mockData.contact.email, href: `mailto:${mockData.contact.email}` },
    { icon: <MapPin className="h-5 w-5" />, title: "Service Area", content: mockData.contact.serviceArea },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 tex-grid opacity-60" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Info */}
          <div className="lg:col-span-2">
            <p className="label-kicker mb-4">Get In Touch</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-6">
              Let&apos;s Talk
              <span className="block text-primary">About Your Site</span>
            </h2>
            <p className="text-gray-400 leading-relaxed mb-10">
              Ready to discuss your demolition project? Reach out for a free
              consultation. We respond fast and bring straight answers — no
              runaround.
            </p>
            <div className="space-y-4">
              {info.map((item) => {
                const inner = (
                  <div className="flex items-center gap-4 card-industrial p-5">
                    <div className="w-11 h-11 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {item.title}
                      </div>
                      <div className="text-white font-semibold">{item.content}</div>
                    </div>
                  </div>
                );
                return item.href ? (
                  <a key={item.title} href={item.href} className="block">
                    {inner}
                  </a>
                ) : (
                  <div key={item.title}>{inner}</div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="card-industrial p-8 md:p-10">
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white mb-6 flex items-center gap-3">
                <Send className="h-6 w-6 text-primary" /> Send Us a Message
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="label-kicker mb-2 block">Full Name *</Label>
                    <Input
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="John Smith"
                      className="rounded-none h-12 bg-background border-border focus-visible:border-primary focus-visible:ring-primary"
                      required
                    />
                  </div>
                  <div>
                    <Label className="label-kicker mb-2 block">Email *</Label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="you@email.com"
                      className="rounded-none h-12 bg-background border-border focus-visible:border-primary focus-visible:ring-primary"
                      required
                    />
                  </div>
                  <div>
                    <Label className="label-kicker mb-2 block">Phone</Label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="(703) 000-0000"
                      className="rounded-none h-12 bg-background border-border focus-visible:border-primary focus-visible:ring-primary"
                    />
                  </div>
                  <div>
                    <Label className="label-kicker mb-2 block">Subject</Label>
                    <Input
                      data-testid="contact-subject-input"
                      value={formData.subject}
                      onChange={(e) => handleChange("subject", e.target.value)}
                      placeholder="How can we help?"
                      className="rounded-none h-12 bg-background border-border focus-visible:border-primary focus-visible:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Message *</Label>
                  <Textarea
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    placeholder="Tell us about your project..."
                    rows={5}
                    className="rounded-none bg-background border-border focus-visible:border-primary focus-visible:ring-primary"
                    required
                  />
                </div>
                <Button
                  data-testid="contact-submit-btn"
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="rounded-none font-bold uppercase tracking-widest px-8 py-6 w-full sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" /> Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
